import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { ColorItem, PopupData, Service } from 'webapp-types';

import ColorBlockComponent from 'webapp/components/color-block';
import CommonPopupsColorPickerComponent from 'webapp/components/common/popups/color-picker';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import type { Args } from 'webapp/components/popup/inner/context';
import PopupInnerContextComponent from 'webapp/components/popup/inner/context';

export default class CommonPopupsPopupColorsAndThemesComponent extends PopupInnerContextComponent<PopupData.Common.ColorsAndThemes> {
	@service('constants') declare constantsService: Service.Constants;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('popup') declare popupService: Service.Popup;

	@tracked selectedColor = '';
	@tracked selectedTheme = 0;
	@tracked applyColorToAllSlides = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.selectedColor = this.popupData.color;
		this.selectedTheme = this.popupData.theme;
	}

	get availableColors() {
		return this.constantsService.boardColors.map((color: ColorItem) => {
			return {
				hex: color.hex,
				selected: color.hex === this.selectedColor,
			};
		});
	}

	get availableThemes() {
		return this.mmBoardsService
			.availableThemesForColor(this.selectedColor)
			.map((themeId: number) => {
				return {
					id: themeId,
					selected: themeId === this.selectedTheme,
				};
			});
	}

	get isThemesSectionShown() {
		return !this.popupData.reportSlide;
	}

	get isApplyColorToAllSlidesShown() {
		return !!this.popupData.reportSlide;
	}

	get allowCustom() {
		return !!this.popupData.allowCustom;
	}

	get selectedOption() {
		return this.availableColors.find((item) => item.selected);
	}

	get isCustomSelected() {
		return this.selectedColor && !this.selectedOption;
	}

	checkIfSelectedThemeIsAvailable() {
		const themeIds = this.availableThemes.map((theme) => theme.id);
		let theme = this.selectedTheme;

		if (!themeIds.includes(theme)) {
			theme = themeIds[0]!;
			this.changeTheme(theme);
		}
	}

	@action
	changeColor(color: string) {
		this.selectedColor = color;

		if (this.popupData.color !== color) {
			this.applyColorToAllSlides = false;
		}

		this.checkIfSelectedThemeIsAvailable();
		this.popupData.onColorChange(color);
	}

	@action
	changeTheme(theme: number) {
		this.selectedTheme = theme;
		this.popupData.onThemeChange(theme);
	}

	@action
	changeApplyColorToAllSlides(value: boolean) {
		const report = this.popupData.reportSlide?.report;

		if (!report) {
			return;
		}

		this.applyColorToAllSlides = value;

		if (value) {
			this.mmReportsService.applyVisualSettingsToAllSlides(report, {
				color: this.selectedColor,
				themeId: this.selectedTheme,
			});
		}
	}

	@action
	openColorPicker(e: PointerEvent) {
		const attachTo = e.target as HTMLElement;
		if (!attachTo) {
			return;
		}

		const popup = this.popupService.createContext(CommonPopupsColorPickerComponent, attachTo, {
			popupClass: 'popup-type-color-picker',
			theme: this.args.model.theme,
		});

		return popup.open({
			value: this.selectedColor,
			onChange: this.changeCustomColor,
			hasSubmit: true,
		});
	}

	@action
	changeCustomColor(color: string) {
		if (color) {
			let hex = color;
			if (!hex.startsWith('#')) {
				hex = `#${hex}`;
			}
			this.changeColor(hex);
		}
	}
	<template>
		<a role="button" class="close" {{on "click" this.close}}>
			<CommonUiIconComponent @name="Close" @size="sm" />
		</a>

		<div class="context-ct">
			<span>Background color</span>
			<ul class="colors">
				{{#each this.availableColors as |color|}}
					<li class={{if color.selected "selected"}}>
						<a role="button" {{on "click" (fn this.changeColor color.hex)}}>
							<ColorBlockComponent @color={{color.hex}} />
						</a>
					</li>
				{{/each}}
				{{#if this.allowCustom}}
					<li class="custom-color {{if this.isCustomSelected 'selected'}}">
						<a role="button" {{on "click" this.openColorPicker}}>
							<ColorBlockComponent @color="" />
						</a>
					</li>
				{{/if}}
			</ul>

			{{#if this.isThemesSectionShown}}
				<hr />
				<span>Available chart themes</span>
				<ul class="themes">
					{{#each this.availableThemes as |theme|}}
						<li class={{if theme.selected "selected"}}>
							<a
								role="button"
								{{on "click" (fn this.changeTheme theme.id)}}
								class="theme theme-{{theme.id}}"
							>
								<span></span>
							</a>
						</li>
					{{/each}}
				</ul>
			{{/if}}

			{{#if this.isApplyColorToAllSlidesShown}}
				<hr />
				<div class="apply-checkbox">
					<FormCheckboxComponent
						@name="apply_color_to_all_slides"
						@selected={{this.applyColorToAllSlides}}
						@label="Apply to all slides"
						@theme="dark"
						@onChange={{this.changeApplyColorToAllSlides}}
					/>
				</div>
			{{/if}}
		</div>
	</template>
}
